/**
 * Singleton composable for review assignments management state.
 *
 * Provides shared data and filters for the admin assignments management page
 * (ReviewAssignments.vue at /review-assignments).
 *
 * Fetches assignment entries from the review_assignments table and users
 * with reviews access from the auth service.
 */
import { ref } from "vue";
import api from "@/utils/api.js";

// --- Singleton state — module-level refs so all consumers share the same instance ---

// All assignment entries returned by the backend
const assignmentEntries = ref([]);

// Active users who have "reviews" in their allowed_apps, formatted as { id, role, display } options
const reviewUsers = ref([]);

// True while fetchData() is in progress
const loading = ref(false);

// Error message string if the last fetchData() call failed, empty string otherwise
const error = ref("");

// Currently selected month in YYYY-MM format — used by the calendar to control which month is shown
const selectedMonth = ref(new Date().toISOString().slice(0, 7));

// Snackbar notification state — show/hide, message text, and color (success/error/etc.)
const snackbar = ref({ show: false, message: "", color: "success" });

/**
 * Shared filter state — consumed by the assignments table page.
 */
const filters = ref({
  dateFrom: null,   // ISO date string "YYYY-MM-DD" or null — lower bound
  dateTo: null,     // ISO date string "YYYY-MM-DD" or null — upper bound
  location: [],     // Array of location name strings — empty means "all locations"
  platform: null,   // Platform name string or null — null means "all platforms"
  brand: [],        // Array of brand name strings — empty means "all brands"
  user_id: null,    // Assigned user ID (integer) or null — null means "all users"
});

export function useReviewAssignments() {
  /**
   * Trigger a snackbar notification.
   */
  function showSnack(message, color = "success") {
    snackbar.value = { show: true, message, color };
  }

  /**
   * Fetch all data needed by the assignments table.
   *
   * Makes two parallel API calls:
   *   1. GET /api/reviews/admin/assignments — all review assignments
   *   2. GET /api/admin/users — all users from the auth service
   */
  async function fetchData() {
    loading.value = true;
    error.value = "";

    try {
      const [assignmentsRes, usersRes] = await Promise.all([
        api.marketingApi.get("/api/marketing/reviews/admin/assignments"),
        api.authApi.get("/api/admin/users"),
      ]);

      // Sort entries ascending: date first, then platform and location as tiebreakers
      const entries = assignmentsRes.data.entries || [];
      entries.sort((a, b) => {
        const da = typeof a.assignment_date === "string" ? a.assignment_date : "";
        const db = typeof b.assignment_date === "string" ? b.assignment_date : "";
        return da.localeCompare(db) || a.platform.localeCompare(b.platform) || a.location.localeCompare(b.location);
      });
      assignmentEntries.value = entries;

      const users = usersRes.data || [];

      // Predicate: user must be active and have "reviews" in their allowed_apps
      const hasReviews = (u) =>
        u.account_status === "active" &&
        u.allowed_apps &&
        (Array.isArray(u.allowed_apps)
          ? u.allowed_apps.includes("reviews")
          : JSON.parse(u.allowed_apps || "[]").includes("reviews"));

      // Map each qualifying user to a flat option object for use in dropdowns and display
      const toOption = (u) => ({
        id: u.id,
        role: u.role,
        display: u.first_name
          ? `${u.first_name}${u.last_name ? " " + u.last_name : ""} (${u.email})`
          : u.email,
      });

      reviewUsers.value = users
        .filter(hasReviews)
        .map(toOption)
        .sort((a, b) => a.display.localeCompare(b.display));
    } catch (err) {
      console.error("Failed to fetch data:", err);
      error.value = err.response?.data?.error || "Failed to load data";
    } finally {
      loading.value = false;
    }
  }

  return {
    // --- Data (fetched by fetchData) ---
    assignmentEntries, // All review assignment entries
    reviewUsers,       // Active users with reviews access, formatted as display options
    loading,           // True while fetchData() is in progress
    error,             // Error message from the last failed fetchData(), or ""
    selectedMonth,     // Currently viewed month in YYYY-MM format
    snackbar,          // Snackbar notification state { show, message, color }

    // --- Shared filters ---
    filters,

    // --- Methods ---
    showSnack,  // Trigger a snackbar notification
    fetchData,  // Load assignment entries and users from the API
  };
}
